import { useMemo, useState } from 'react';
import type { CSSObjectWithLabel, GroupBase, StylesConfig } from 'react-select';

import { useGetAllSessions } from '~entities/session/api/get-sessions';
import {
  ALL_SESSIONS_ID,
  ALL_SESSIONS_OPTION,
} from '~features/new-dashboard/constants';

import {
  SELECT_MENU_PORTAL_CLASS,
  defaultClassNames,
  defaultStyles,
} from '~shared/components/select/helper';
import Select from '~shared/components/select/select';
import { useDebounce } from '~shared/shadcn/expansions/multiple-selector';
import { cn } from '~shared/shadcn/lib/utils';

export interface SessionOption {
  label: string;
  value: string;
}

export interface SessionSelectorProps {
  session:
    | {
        label: string;
        value: string;
      }
    | undefined
    | null;
  onChange: (
    value:
      | {
          label: string;
          value: string;
        }
      | undefined
      | null,
  ) => void;
  styles?: StylesConfig<unknown, boolean, GroupBase<unknown>> | undefined;
  disabled?: boolean;
  /** When true, only show sessions with endDate >= today (hide past sessions in dropdown) */
  excludePastSessions?: boolean;
  /** When true, archived sessions appear in search (studio admin flows only; API ignores for parent portal) */
  includeArchivedSessions?: boolean;
  /** When true, prepends an "All Sessions" option (Dashboard V2). */
  includeAllSessions?: boolean;
  /** Max sessions returned from search API (default 100). */
  searchLimit?: number;
  /**
   * Portal the menu (required inside Radix dialogs so the menu is not clipped).
   * Prefer `menuPortalTarget` set to the DialogContent element.
   * Pass `null` while the dialog ref is resolving — do not default to body.
   */
  menuInPortal?: boolean;
  /** Element to portal into. Defaults to document.body only when omitted. */
  menuPortalTarget?: HTMLElement | null;
}

export const SessionSelector = ({
  session,
  onChange,
  styles,
  disabled = false,
  excludePastSessions = false,
  includeArchivedSessions = false,
  includeAllSessions = false,
  searchLimit = 100,
  menuInPortal = false,
  menuPortalTarget = null,
}: SessionSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const { data, isFetching } = useGetAllSessions({
    page: 1,
    limit: searchLimit,
    name: debouncedSearchQuery || undefined,
    excludePastSessions,
    includeArchivedSessions: includeArchivedSessions ? true : undefined,
  });

  const options = useMemo(() => {
    const sessions = data?.sessions ?? [];
    let foundSession = false;
    const optionsFromData = sessions.map((s) => {
      if (s._id === session?.value) {
        foundSession = true;
      }
      return { label: s.name, value: s._id };
    });

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (session != null && !foundSession && session.value !== ALL_SESSIONS_ID) {
      optionsFromData.push({ label: session.label, value: session.value });
    }

    if (includeAllSessions) {
      return [ALL_SESSIONS_OPTION, ...optionsFromData];
    }

    return optionsFromData;
  }, [data, session, includeAllSessions]);

  const currentSession = data?.sessions?.find((s) => s._id === session?.value);

  const currentOption = session
    ? {
        label: currentSession?.name ?? session.label,
        value: session.value || '',
      }
    : undefined;

  const resolvedPortalTarget = menuInPortal
    ? // Explicit null = caller is still resolving a DialogContent ref; do not
      // fall back to body (that reintroduces Radix outside-click dismiss).
      menuPortalTarget === undefined
      ? typeof document !== 'undefined'
        ? document.body
        : null
      : menuPortalTarget
    : null;

  const mergedStyles =
    menuInPortal
      ? {
          ...defaultStyles,
          ...styles,
          menuPortal: (base: CSSObjectWithLabel) => ({
            ...base,
            zIndex: 70,
          }),
        }
      : styles;

  return (
    <Select
      isClearable
      value={currentOption}
      onChange={(option) => {
        const opt = option as SessionOption | null | undefined;
        onChange(opt);
      }}
      placeholder='Select Session'
      options={options}
      isLoading={isFetching}
      onInputChange={(inputValue, { action }) => {
        if (action === 'input-change') {
          setSearchQuery(inputValue);
        }
      }}
      styles={mergedStyles}
      isDisabled={disabled}
      menuPosition={menuInPortal ? 'fixed' : undefined}
      menuPortalTarget={resolvedPortalTarget}
      classNames={
        menuInPortal
          ? {
              ...defaultClassNames,
              menuPortal: (state) =>
                cn(
                  defaultClassNames.menuPortal?.(state),
                  SELECT_MENU_PORTAL_CLASS,
                ),
            }
          : undefined
      }
    />
  );
};
